import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Search, Edit, Trash2, Shield, ShieldCheck, User, Mail, Key, Briefcase, Image, Lock, Upload, Camera } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const UsersPage = () => {
  const { getAuthHeaders, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    is_admin: false,
    is_active: true,
    avatar_url: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeaders()
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or WEBP');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    if (!editUser) {
      toast.error('Please save the user first before uploading a photo');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(
        `${API}/users/${editUser.id}/avatar`,
        formDataUpload,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setFormData(prev => ({ ...prev, avatar_url: response.data.avatar_url }));
      toast.success('Profile photo uploaded successfully');
      fetchUsers();
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editUser) {
        // Only send changed fields for update
        const updateData = {};
        if (formData.name !== editUser.name) updateData.name = formData.name;
        if (formData.email !== editUser.email) updateData.email = formData.email;
        if (formData.role !== editUser.role) updateData.role = formData.role;
        if (formData.is_admin !== editUser.is_admin) updateData.is_admin = formData.is_admin;
        if (formData.is_active !== editUser.is_active) updateData.is_active = formData.is_active;
        if (formData.password) updateData.password = formData.password;
        if (formData.avatar_url !== (editUser.avatar_url || '')) updateData.avatar_url = formData.avatar_url;

        await axios.put(`${API}/users/${editUser.id}`, updateData, {
          headers: getAuthHeaders()
        });
        toast.success('User updated successfully');
      } else {
        // Create new user
        if (!formData.password) {
          toast.error('Password is required for new users');
          return;
        }
        await axios.post(`${API}/users`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('User created successfully');
      }
      fetchUsers();
      resetForm();
    } catch (error) {
      console.error('Failed to save user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: getAuthHeaders()
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: '',
      is_admin: false,
      is_active: true,
      avatar_url: ''
    });
    setEditUser(null);
    setIsCreateOpen(false);
  };

  const openEditDialog = (user) => {
    setEditUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      is_admin: user.is_admin,
      is_active: user.is_active,
      avatar_url: user.avatar_url || ''
    });
    setIsCreateOpen(true);
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.role.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse-slow text-[#007AFF] text-xl">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-[#0F172A] tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            USER MANAGEMENT
          </h1>
          <p className="text-[#475569] mt-1">Manage team members and permissions</p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsCreateOpen(open); }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#007AFF] hover:bg-[#0066CC] text-white font-bold uppercase tracking-wide shadow-lg hover:shadow-xl transition-all"
              data-testid="create-user-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#E2E8F0] text-[#0F172A] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                {editUser ? 'EDIT USER' : 'NEW USER'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., John Smith"
                    className="pl-10 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]"
                    data-testid="user-name-input"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="e.g., john@company.com"
                    className="pl-10 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]"
                    data-testid="user-email-input"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">
                  {editUser ? 'New Password (leave blank to keep current)' : 'Password'}
                </Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editUser ? "Leave blank to keep current" : "Enter password"}
                    className="pl-10 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]"
                    data-testid="user-password-input"
                    required={!editUser}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Role</Label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    placeholder="e.g., Sales Rep, Sales Leader, Account Executive"
                    className="pl-10 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]"
                    data-testid="user-role-input"
                    required
                  />
                </div>
                <p className="text-xs text-[#94A3B8]">Enter any custom role name</p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Profile Photo</Label>
                <div className="flex items-center gap-4 p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                  <div className="relative">
                    <Avatar className="h-16 w-16 border-2 border-[#E2E8F0]">
                      <AvatarImage 
                        src={formData.avatar_url?.startsWith('/uploads') 
                          ? `${BASE_URL}${formData.avatar_url}` 
                          : formData.avatar_url} 
                        alt="Preview" 
                      />
                      <AvatarFallback className="bg-[#EFF6FF] text-[#007AFF] text-xl">
                        {formData.name ? getInitials(formData.name) : <Camera className="w-6 h-6" />}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleAvatarUpload}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || !editUser}
                      className="w-full border-[#E2E8F0] text-[#475569] hover:text-[#007AFF] hover:border-[#007AFF]"
                      data-testid="upload-avatar-btn"
                    >
                      {uploading ? (
                        <>Uploading...</>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Photo
                        </>
                      )}
                    </Button>
                    {!editUser && (
                      <p className="text-xs text-[#F59E0B]">Save user first to upload photo</p>
                    )}
                    <p className="text-xs text-[#94A3B8]">JPEG, PNG, GIF or WEBP. Max 5MB.</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-[#8B5CF6]" />
                  <div>
                    <p className="text-sm font-medium text-[#0F172A]">Admin Access</p>
                    <p className="text-xs text-[#94A3B8]">Can manage users and settings</p>
                  </div>
                </div>
                <Switch
                  checked={formData.is_admin}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_admin: checked })}
                  data-testid="user-admin-switch"
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                <div>
                  <p className="text-sm font-medium text-[#0F172A]">Active Status</p>
                  <p className="text-xs text-[#94A3B8]">User can log in and access system</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  data-testid="user-active-switch"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  className="flex-1 border-[#E2E8F0] text-[#475569] hover:text-[#0F172A] hover:bg-[#F1F5F9]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#007AFF] hover:bg-[#0066CC] text-white font-bold"
                  data-testid="user-submit-btn"
                >
                  {editUser ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search users by name, email, or role..."
              className="pl-10 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]"
              data-testid="search-users-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#E2E8F0] hover:bg-transparent bg-[#F8FAFC]">
                <TableHead className="text-[#475569] font-semibold">User</TableHead>
                <TableHead className="text-[#475569] font-semibold">Role</TableHead>
                <TableHead className="text-[#475569] font-semibold">Status</TableHead>
                <TableHead className="text-[#475569] font-semibold">Admin</TableHead>
                <TableHead className="text-[#475569] font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-[#94A3B8]">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow 
                    key={user.id} 
                    className="border-[#E2E8F0] hover:bg-[#F8FAFC]"
                    data-testid={`user-row-${user.id}`}
                  >
                    <TableCell>
                      <div 
                        className="flex items-center gap-3 cursor-pointer group"
                        onClick={() => openEditDialog(user)}
                      >
                        <Avatar className="h-10 w-10 border border-[#E2E8F0] group-hover:border-[#007AFF] transition-colors">
                          <AvatarImage 
                            src={user.avatar_url?.startsWith('/uploads') 
                              ? `${BASE_URL}${user.avatar_url}` 
                              : user.avatar_url} 
                            alt={user.name} 
                          />
                          <AvatarFallback className="bg-[#EFF6FF] text-[#007AFF]">
                            {getInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-[#0F172A] group-hover:text-[#007AFF] transition-colors">{user.name}</p>
                          <p className="text-xs text-[#94A3B8]">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-[#F1F5F9] text-[#475569] border-0">
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        className={user.is_active 
                          ? 'bg-[#10B981]/10 text-[#059669] border-0' 
                          : 'bg-[#FF3B30]/10 text-[#DC2626] border-0'
                        }
                      >
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.is_admin ? (
                        <div className="flex items-center gap-1 text-[#8B5CF6]">
                          <ShieldCheck className="w-4 h-4" />
                          <span className="text-sm font-medium">Admin</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-[#94A3B8]">
                          <Shield className="w-4 h-4" />
                          <span className="text-sm">User</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(user)}
                          className="text-[#475569] hover:text-[#007AFF] hover:bg-[#EFF6FF]"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {user.id !== currentUser?.id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(user.id)}
                            className="text-[#475569] hover:text-[#FF3B30] hover:bg-[#FEF2F2]"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Stats */}
      <div className="flex items-center justify-between text-sm text-[#94A3B8]">
        <span>Showing {filteredUsers.length} of {users.length} users</span>
        <div className="flex gap-4">
          <span>Active: {users.filter(u => u.is_active).length}</span>
          <span>Admins: {users.filter(u => u.is_admin).length}</span>
        </div>
      </div>
    </div>
  );
};

export default UsersPage;
